<template>
  <v-container fluid>
    <v-row align="center" justify="center">
      <v-col v-if="artworkResp.src" cols="auto">
        <v-img
          :aspect-ratio="aspectRatio"
          :sizes="artworkResp.sizes"
          :src="artworkResp.src"
          :srcset="artworkResp.srcset"
          alt="Artwork for banner"
          cover
          :max-width="compact ? '60' : '80'"
          :width="compact ? '60' : '80'"
        />
      </v-col>

      <v-col
        :class="
          compact
            ? 'd-flex flex-row align-center justify-space-between ga-2'
            : 'd-flex flex-column ga-2'
        "
      >
        <div>
          <h3
            v-if="value.title"
            :class="
              compact
                ? 'text-1 font-weight-medium'
                : 'text-h6 text-sm-h5 font-weight-medium'
            "
          >
            <StyledText :value="value.title" :em-class="emClass" />
          </h3>

          <!-- eslint-disable vue/no-v-html -->
          <div
            v-if="value.description"
            :class="compact ? 'text-body-2 mt-1' : 'text-body-1 mt-2'"
            v-html="value.description"
          ></div>
        </div>

        <div v-if="ctas.length" class="d-flex flex-wrap ga-2">
          <CtaButton
            v-for="(cta, i) in ctas"
            :key="cta.id"
            :primary-color="primaryButtonColor"
            :secondary="i > 0"
            :secondary-color="secondaryButtonColor"
            :size="compact ? 'small' : 'large'"
            :value="cta.ctas_id"
            class="text-none"
            rounded="pill"
          />
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import type { components } from '#root/types/directus'
import type { DirectusSchema } from '#root/types/content'
import { computed, toRef } from 'vue'
import { useImgResponsive } from '#root/lib/img'

type BannerCta = components['schemas']['ItemsBannersCtas'] & {
  ctas_id: DirectusSchema['cta']
}

const props = defineProps<{
  compact?: boolean
  value: components['schemas']['ItemsBanners']
}>()

const aspectRatio = 1
const artworkResp = useImgResponsive(
  toRef(() => ({
    aspectRatio,
    image:
      props.value.artwork && typeof props.value.artwork === 'object'
        ? props.value.artwork
        : undefined,
    sizes: {
      xs: '400px'
    }
  }))
)

const colorScheme = computed(() => {
  const scheme = props.value.color_scheme
  return scheme && typeof scheme === 'object' ? scheme : null
})

const emClass = computed(() => {
  const emColor = colorScheme.value?.em_text_color
  if (emColor && typeof emColor === 'object' && emColor.id) {
    return `text-${emColor.id} font-weight-medium`
  }
  return 'text-light-blue-darken-2 font-weight-medium'
})

const primaryButtonColor = computed(() => {
  const color = colorScheme.value?.primary_button_color
  return color && typeof color === 'object' ? color.id : undefined
})

const secondaryButtonColor = computed(() => {
  const color = colorScheme.value?.secondary_button_color
  return color && typeof color === 'object' ? color.id : undefined
})

const ctas = computed(() => {
  return (
    props.value.ctas?.filter((cta): cta is BannerCta => {
      return (
        typeof cta === 'object' &&
        cta !== null &&
        typeof cta.ctas_id === 'object' &&
        cta.ctas_id !== null &&
        cta.ctas_id.status === 'published'
      )
    }) ?? []
  )
})
</script>
